import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Clock, MapPin, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateWithDay, formatTimeRange } from '@/utils/formatters';

const PartialExamsDateView = ({ moduleFilter = 'ALL', timeFilter = 'active' }) => {
  const [groupedData, setGroupedData] = useState({});
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [modalSlots, setModalSlots] = useState([]);
  const [showSlotsModal, setShowSlotsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingModalSlots, setLoadingModalSlots] = useState(false);

  useEffect(() => {
    fetchAndGroupExams();
  }, [moduleFilter, timeFilter]);

  const fetchAndGroupExams = async () => {
    setLoading(true);
    try {
      // Fetch partial exams with time filter
      const response = await adminAPI.getAvailablePartialSlots({ time_filter: timeFilter });
      const allExams = response.data;
      
      // Filter by module if not "ALL"
      const filteredExams = moduleFilter === 'ALL' 
        ? allExams 
        : allExams.filter(exam => exam.module === moduleFilter);
      
      // Group by module, date, and category
      const grouped = {};
      
      filteredExams.forEach(exam => {
        const module = exam.module;
        const date = exam.exam_date;
        const category = exam.category || 'SHARED'; // PB, CD, or SHARED (for Speaking)
        
        // Create unique key: module_date_category (e.g., "WRITING_2025-12-16_PB")
        const groupKey = `${module}_${date}_${category}`;
        
        if (!grouped[module]) {
          grouped[module] = {};
        }
        
        if (!grouped[module][groupKey]) {
          grouped[module][groupKey] = {
            date: date,
            module: module,
            category: category,
            total_slots: 0,
            booked_count: 0,
            available_slots: 0,
            exams: []
          };
        }
        
        const remaining = exam.remaining_seats || 0;
        
        // For SPEAKING, count each exam as 1 slot (time slot)
        // For other modules, count by total_slots (seat capacity)
        if (module === 'SPEAKING') {
          // Each speaking exam is one time slot
          grouped[module][groupKey].total_slots += 1;
          grouped[module][groupKey].booked_count += (remaining === 0 ? 1 : 0);
          grouped[module][groupKey].available_slots += (remaining > 0 ? 1 : 0);
        } else {
          // For L/R/W modules, count by seat capacity
          const total = exam.total_slots || 1;
          const booked = total - remaining;
          
          grouped[module][groupKey].total_slots += total;
          grouped[module][groupKey].booked_count += booked;
          grouped[module][groupKey].available_slots += remaining;
        }
        
        grouped[module][groupKey].exams.push(exam);
      });
      
      setGroupedData(grouped);
    } catch (error) {
      console.error('Failed to fetch exams:', error);
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchSlotsForDate = async (module, date, category) => {
    setLoadingModalSlots(true);
    setSelectedSlot({ module, date, category });
    setShowSlotsModal(true);
    
    try {
      if (module === 'SPEAKING') {
        // Use speaking-specific endpoint
        const response = await adminAPI.getSpeakingSlotsByDate(date);
        setModalSlots(response.data);
      } else {
        // For L/R/W modules - fetch seat-based bookings for ALL time slots
        // Use groupKey to get the correct category-specific data
        const groupKey = `${module}_${date}_${category || 'SHARED'}`;
        const dateData = groupedData[module]?.[groupKey];
        if (dateData && dateData.exams.length > 0) {
          // Process ALL exams (time slots) for this date
          const examsWithSeats = [];
          
          for (const exam of dateData.exams) {
            // Fetch all bookings for this exam
            try {
              const bookingResponse = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/admin/partial-exam/${exam.id}/bookings`,
                {
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                  }
                }
              );
              
              if (bookingResponse.ok) {
                const bookings = await bookingResponse.json();
                
                // Create seat list (1 to total_slots)
                const totalSeats = exam.total_slots || 20;
                const seats = [];
                
                // Filter only BOOKED bookings
                const bookedBookings = bookings.filter(b => b.status === 'BOOKED');
                
                for (let i = 1; i <= totalSeats; i++) {
                  // Check if this seat number has a booking
                  const booking = bookedBookings[i - 1]; // Bookings are in order
                  
                  if (booking) {
                    seats.push({
                      seat_number: i,
                      is_booked: true,
                      student_name: booking.student_name || 'Student',
                      student_email: booking.student_email || '',
                      booked_by_type: booking.is_other_student ? 'Manual-Institute' : 'Self'
                    });
                  } else {
                    seats.push({
                      seat_number: i,
                      is_booked: false
                    });
                  }
                }
                
                examsWithSeats.push({
                  ...exam,
                  seats: seats,
                  total_seats: totalSeats
                });
              }
            } catch (err) {
              console.error('Failed to fetch bookings for exam:', exam.id, err);
            }
          }
          
          setModalSlots(examsWithSeats);
        }
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to load slots');
      setModalSlots([]);
    } finally {
      setLoadingModalSlots(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!confirm('Are you sure you want to delete this slot?')) return;
    
    try {
      await adminAPI.deletePartialExam(slotId);
      toast.success('Slot deleted successfully');
      
      // Refresh data
      await fetchAndGroupExams();
      if (selectedSlot) {
        await fetchSlotsForDate(selectedSlot.module, selectedSlot.date, selectedSlot.category);
      }
    } catch (error) {
      console.error('Failed to delete slot:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete slot');
    }
  };

  const getModuleColor = (module) => {
    const colors = {
      'LISTENING': 'bg-blue-600',
      'READING': 'bg-green-600',
      'WRITING': 'bg-purple-600',
      'SPEAKING': 'bg-orange-600'
    };
    return colors[module] || 'bg-gray-600';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        <p className="mt-4 text-gray-600">Loading exams...</p>
      </div>
    );
  }

  if (Object.keys(groupedData).length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <p className="text-gray-600 text-lg font-semibold">No exams created yet</p>
        <p className="text-gray-500 mt-2">Create exams in the &quot;Create Exam&quot; tab</p>
      </div>
    );
  }

  return (
    <>
      {/* Module-wise Date List View */}
      <div className="space-y-6">
        {Object.keys(groupedData).sort().map(module => (
          <div key={module} className="space-y-3">
            {/* Module Header */}
            <div className="flex items-center gap-3 mb-3">
              <Badge className={`${getModuleColor(module)} text-white text-base px-4 py-1`}>
                {module}
              </Badge>
              <span className="text-sm text-gray-500">
                {Object.keys(groupedData[module]).length} date(s)
              </span>
            </div>
            
            {/* Date Rows for this Module */}
            {Object.keys(groupedData[module]).sort().map(groupKey => {
              const dateData = groupedData[module][groupKey];
              const isFull = dateData.available_slots === 0;
              const hasBookings = dateData.booked_count > 0;
              const date = dateData.date; // Extract actual date from data
              
              return (
                <div
                  key={groupKey}
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <Badge className={isFull ? 'bg-orange-500' : hasBookings ? 'bg-red-600' : 'bg-green-600'}>
                          {isFull ? 'Full' : hasBookings ? 'Partially Booked' : 'Available'}
                        </Badge>
                        {/* Category Badge - Show for non-Speaking modules */}
                        {module !== 'SPEAKING' && dateData.category && (
                          <Badge className="bg-purple-600 text-white">
                            {dateData.category}
                          </Badge>
                        )}
                        <span className="font-semibold text-lg">{formatDateWithDay(date)}</span>
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {module} Module • {dateData.booked_count} booked, {dateData.available_slots} available • Total: {dateData.total_slots} slots
                      </div>
                    </div>
                  </div>

                  {/* View Slots/Seats Button */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchSlotsForDate(module, date, dateData.category)}
                      className="bg-white"
                    >
                      {module === 'SPEAKING' ? 'View Slots' : 'View Seats'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Slots Modal */}
      <Dialog open={showSlotsModal} onOpenChange={setShowSlotsModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedSlot?.module} {selectedSlot?.module === 'SPEAKING' ? 'Slots' : 'Seats'} - {selectedSlot && formatDateWithDay(selectedSlot.date)}
            </DialogTitle>
            <DialogDescription>
              {selectedSlot?.module === 'SPEAKING' 
                ? 'View and manage all slots for this date' 
                : 'View seat bookings for this exam'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {loadingModalSlots ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                <p className="mt-4 text-gray-600">Loading...</p>
              </div>
            ) : modalSlots.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg font-semibold">No data found</p>
              </div>
            ) : selectedSlot?.module === 'SPEAKING' ? (
              // Speaking Slots View (Time-based)
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modalSlots.map((slot) => {
                  const isBooked = slot.is_booked || slot.remaining_seats === 0;
                  
                  return (
                    <div
                      key={slot.id}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isBooked
                          ? 'bg-orange-50 border-orange-300'
                          : 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-300'
                      }`}
                    >
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge className={isBooked ? 'bg-orange-500' : 'bg-green-600'}>
                            {isBooked ? 'Booked' : 'Available'}
                          </Badge>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSlot(slot.id);
                            }}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="font-semibold">{formatTimeRange(slot.start_time, slot.end_time)}</span>
                          </div>
                          {slot.room && (
                            <div className="flex items-center gap-2 text-sm">
                              <MapPin className="w-4 h-4 text-gray-500" />
                              <span>{slot.room}</span>
                              {slot.campus && <span className="text-gray-500">• {slot.campus}</span>}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            Seats: {slot.remaining_seats || 0}/{slot.total_slots || 1}
                          </div>
                        </div>
                        
                        {isBooked && slot.booked_by && (
                          <div className="mt-2 pt-3 border-t border-orange-200">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-xs text-gray-600">Booked by:</p>
                              {slot.booked_by.type && (
                                <Badge variant="outline" className="text-xs">
                                  {slot.booked_by.type}
                                </Badge>
                              )}
                            </div>
                            <div className="bg-white/70 p-2 rounded">
                              <p className="text-sm font-semibold text-orange-700">{slot.booked_by.name}</p>
                              {slot.booked_by.email && slot.booked_by.email !== 'N/A' && (
                                <p className="text-xs text-gray-600 mt-0.5">{slot.booked_by.email}</p>
                              )}
                              {slot.booked_by.phone && slot.booked_by.phone !== 'N/A' && (
                                <p className="text-xs text-gray-600">{slot.booked_by.phone}</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Seat-based View (L/R/W Modules) - Multiple Time Slots
              <div className="space-y-8">
                {modalSlots.map((exam, examIndex) => {
                  const bookedCount = exam.seats?.filter(s => s.is_booked).length || 0;
                  const totalSeats = exam.total_seats || exam.seats?.length || 0;
                  const availableCount = totalSeats - bookedCount;
                  
                  return (
                    <div key={exam.id} className="border-b last:border-b-0 pb-6 last:pb-0">
                      {/* Time Slot Header */}
                      <div className="flex items-center justify-between mb-4 bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-purple-600" />
                            <h3 className="font-bold text-lg text-gray-800">
                              {formatTimeRange(exam.start_time, exam.end_time)}
                            </h3>
                          </div>
                          {exam.room && (
                            <div className="flex items-center gap-1 text-sm text-gray-600">
                              <MapPin className="w-4 h-4" />
                              <span>{exam.room}</span>
                            </div>
                          )}
                          {exam.campus && (
                            <span className="text-sm text-gray-500">• {exam.campus}</span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="text-sm">
                            <Badge className="bg-orange-500">{bookedCount} Booked</Badge>
                            <Badge className="bg-green-600 ml-2">{availableCount} Available</Badge>
                          </div>
                          <Button
                            onClick={() => handleDeleteSlot(exam.id)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                      
                      {/* Seats Grid */}
                      {exam.seats && (
                        <div className="grid grid-cols-4 md:grid-cols-5 lg:grid-cols-8 gap-3">
                          {exam.seats.map((seat) => (
                            <div
                              key={seat.seat_number}
                              className={`p-3 rounded-lg border-2 text-center transition-all hover:shadow-md ${
                                seat.is_booked
                                  ? 'bg-orange-50 border-orange-300'
                                  : 'bg-green-50 border-green-300'
                              }`}
                            >
                              <div className="font-bold text-lg mb-1">
                                {seat.seat_number}
                              </div>
                              {seat.is_booked ? (
                                <div className="text-xs">
                                  <Badge className="bg-orange-500 text-white mb-1">Booked</Badge>
                                  <div className="mt-2 text-left bg-white/70 p-2 rounded">
                                    <p className="font-semibold text-orange-700 truncate" title={seat.student_name}>
                                      {seat.student_name}
                                    </p>
                                    <p className="text-gray-600 mt-1">
                                      by <span className="font-semibold">{seat.booked_by_type}</span>
                                    </p>
                                  </div>
                                </div>
                              ) : (
                                <Badge className="bg-green-600 text-white">Available</Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PartialExamsDateView;
